import React, { useState } from 'react';
import WordCloud from 'react-d3-cloud';

type WordCloudWord = {
  text: string;
  value: number;
};

type Props = {
  keyword: string;
  leftLabel: string;
  rightLabel: string;
  common: string[];
  leftOnly: string[];
  rightOnly: string[];
  leftCloudWords: WordCloudWord[];
  rightCloudWords: WordCloudWord[];
};

const ComparisonColumn: React.FC<{
  title: string;
  count: number;
  items: string[];
  accentClass: string;
}> = ({ title, count, items, accentClass }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (items.length === 0) return;

    try {
      await navigator.clipboard.writeText(items.join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch (error) {
      console.error('コピーに失敗:', error);
      alert('コピーに失敗しました。');
    }
  };

  return (
  <section className="bg-white rounded-lg shadow-sm p-5 min-w-0">
    <div className="flex items-center justify-between border-b pb-2 mb-3">
      <h2 className="font-bold text-gray-800">{title}</h2>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-semibold px-2 py-1 rounded ${accentClass}`}>
          {count}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          disabled={items.length === 0}
          title={`${title}をコピー`}
          aria-label={`${title}をコピー`}
          className={`h-8 rounded border px-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
            copied
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'w-8 border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-800'
          }`}
        >
          {copied ? (
            <span className="whitespace-nowrap">コピー済み</span>
          ) : (
            <i className="fas fa-copy text-sm"></i>
          )}
        </button>
      </div>
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-gray-500">該当なし</p>
    ) : (
      <ul className="space-y-1 text-sm">
        {items.slice(0, 80).map((item) => (
          <li key={item} className="break-words text-gray-700">
            {item}
          </li>
        ))}
      </ul>
    )}
  </section>
  );
};

const DifferenceWordCloud: React.FC<{
  title: string;
  words: WordCloudWord[];
  accentClass: string;
}> = ({ title, words, accentClass }) => (
  <section className="bg-white rounded-lg shadow-sm p-5 min-w-0">
    <div className="flex items-center justify-between border-b pb-2 mb-3">
      <h2 className="font-bold text-gray-800">{title}</h2>
      <span className={`text-xs font-semibold px-2 py-1 rounded ${accentClass}`}>
        {words.length}語
      </span>
    </div>

    {words.length === 0 ? (
      <div className="h-40 flex items-center justify-center text-sm text-gray-500">
        クラウド化できる語がありません
      </div>
    ) : (
      <div className="h-64 overflow-hidden">
        <WordCloud
          data={words}
          font="Impact"
          fontWeight="bold"
          fontSize={(word) => Math.max(12, Math.min(54, word.value * 10 + 8))}
          height={240}
          padding={2}
          rotate={0}
        />
      </div>
    )}
  </section>
);

const ComparisonView: React.FC<Props> = ({
  keyword,
  leftLabel,
  rightLabel,
  common,
  leftOnly,
  rightOnly,
  leftCloudWords,
  rightCloudWords,
}) => {
  const totalUnique = common.length + leftOnly.length + rightOnly.length;
  const commonRate = totalUnique === 0 ? 0 : Math.round((common.length / totalUnique) * 100);

  return (
    <div className="space-y-4">
      <section className="bg-white rounded-lg shadow-sm p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-500">キーワード</p>
            <p className="text-lg font-bold text-gray-800 break-words">{keyword}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">共通率</p>
            <p className="text-lg font-bold text-emerald-700">{commonRate}%</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">{leftLabel}のみ</p>
            <p className="text-lg font-bold text-blue-700">{leftOnly.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">{rightLabel}のみ</p>
            <p className="text-lg font-bold text-purple-700">{rightOnly.length}</p>
          </div>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <DifferenceWordCloud
          title={`${leftLabel}だけのワードクラウド`}
          words={leftCloudWords}
          accentClass="bg-blue-100 text-blue-800"
        />
        <DifferenceWordCloud
          title={`${rightLabel}だけのワードクラウド`}
          words={rightCloudWords}
          accentClass="bg-purple-100 text-purple-800"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <ComparisonColumn
          title="共通"
          count={common.length}
          items={common}
          accentClass="bg-emerald-100 text-emerald-800"
        />
        <ComparisonColumn
          title={`${leftLabel}だけ`}
          count={leftOnly.length}
          items={leftOnly}
          accentClass="bg-blue-100 text-blue-800"
        />
        <ComparisonColumn
          title={`${rightLabel}だけ`}
          count={rightOnly.length}
          items={rightOnly}
          accentClass="bg-purple-100 text-purple-800"
        />
      </div>
    </div>
  );
};

export default ComparisonView;
